import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  FiFileText,
  FiChevronDown,
  FiChevronUp,
  FiSearch,
} from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import CustomNavbar from "../CustomPermissions/CustomNavbar";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../../config/api";

const InvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]); // For filtered data
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(1); // Default companyId set to 1
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const getCurrentFY = () => {
    const now = new Date();
    return (now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1).toString();
  };

  const [filterYear, setFilterYear] = useState(getCurrentFY());
  const [loading, setLoading] = useState(false);
  const [editFileId, setEditFileId] = useState(null);

  const [editInvoiceId, setEditInvoiceId] = useState(null);
  const [totalReceived, setTotalReceived] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPending, setTotalPending] = useState(0);

  const [accountDetails, setAccountDetails] = useState([]);
  const [showAccountDetails, setShowAccountDetails] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    calculateTotals();
  }, [filteredInvoices, filterYear, accountDetails]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(
        getApiUrl("/PartnerFiles/companies/"),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      const filteredCompanies = response.data.data.filter(
        (company) =>
          company.name !== "Docladder Digital" &&
          company.name !== "De-Addiction"
      );
      setCompanies(filteredCompanies);

      // If initial fetch, set first company as default
      if (filteredCompanies.length > 0 && selectedCompany === 1) {
        fetchInvoicesByCompany(filteredCompanies[0].id);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  
  const fetchInvoicesByCompany = async (companyId) => {
    try {
      setLoading(true);

      // Fetch invoices for the selected company
      const invoicesResponse = await axios.get(
        getApiUrl(`/Invoice/InvoiceList/?company=${companyId}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      // Fetch account details for the selected company
      const accountDetailsResponse = await axios.get(
        getApiUrl(`/PartnerFiles/company/?id=${companyId}`),
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      setInvoices(invoicesResponse.data);
      setFilteredInvoices(invoicesResponse.data); // Initialize filtered invoices
      setSelectedCompany(companyId);

      // Set account details for the selected company
      setAccountDetails(accountDetailsResponse.data.data); // Assuming you add a new state for account details
      setLoading(false);
    } catch (error) {
      console.error("Error fetching invoices or account details:", error);
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    let received = 0;
    let total = 0;
    let pending = 0;

    filteredInvoices.forEach((invoice) => {
      const receivedAmount = parseFloat(invoice.rec_amount) || 0;
      const invoiceTotal = parseFloat(invoice.amount) || 0;
      const pendingAmount = parseFloat(invoice.pending_amount) || 0;

      received += receivedAmount;
      total += invoiceTotal;
      pending += pendingAmount;
    });

    // Calculate total pending amount excluding "Received" invoices
    const totalPending = filteredInvoices
      .filter((invoice) => invoice.status !== "Received")
      .reduce(
        (total, invoice) => total + (parseFloat(invoice.pending_amount) || 0),
        0
      );

    setTotalReceived(received);
    setTotalAmount(total);
    setTotalPending(totalPending);
  };

  const [invoiceDetails, setInvoiceDetails] = useState({
    invoice_number: "",
    invoice_date: "",
    hospital_name: "",
    location: "",
    candidate: "",
    des: "",
    amount: "",
    pending_amount: "",
    expected_payment: "",
    rec_amount: "",
    status: "",
    // company: "",
    company: selectedCompany.toString(),
    file: null,
  });

  const availableYears = useMemo(() => {
    const years = new Set();
    invoices.forEach(invoice => {
      if (invoice.invoice_date) {
        const [y, m, d] = invoice.invoice_date.split('-').map(Number);
        const fy = m >= 4 ? y : y - 1;
        if (fy) years.add(fy.toString());
      }
    });
    return Array.from(years).sort((a, b) => b - a); // Sort descending
  }, [invoices]);

  // Handle search and filter logic
  useEffect(() => {
    let filtered = invoices;

    if (searchQuery.trim() !== "") {
      filtered = filtered.filter((invoice) => {
        return (
          (invoice.invoice_number && String(invoice.invoice_number).toLowerCase().includes(searchQuery.toLowerCase())) ||
          (invoice.hospital_name && invoice.hospital_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (invoice.status && invoice.status.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (invoice.company__name && invoice.company__name.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      });
    }

    if (filterYear !== "") {
      const fyStart = parseInt(filterYear);
      filtered = filtered.filter((invoice) => {
        if (!invoice.invoice_date) return false;
        const [y, m, d] = invoice.invoice_date.split('-').map(Number);
        const invoiceFY = m >= 4 ? y : y - 1;
        return invoiceFY === fyStart;
      });
    }

    if (filterMonth !== "") {
      filtered = filtered.filter((invoice) => {
        if (!invoice.invoice_date) return false;
        const parts = invoice.invoice_date.split('-');
        return parts.length >= 2 && parts[1] === filterMonth;
      });
    }

    setFilteredInvoices(filtered);
  }, [searchQuery, filterMonth, filterYear, invoices]);

  const toggleRowExpansion = (id) => {
    setExpandedRowId(expandedRowId === id ? null : id);
  };

  const [modalOpen, setModalOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editInvoiceId
      ? getApiUrl(`/Invoice/InvoiceDetail/${editInvoiceId}/`)
      : getApiUrl("/Invoice/InvoiceList/");
    const method = editInvoiceId ? "put" : "post";

    try {
      await axios({
        method,
        url,
        data: invoiceDetails,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      // fetchInvoicesByCompany(1);
      fetchInvoicesByCompany(selectedCompany);
      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving invoice:", error);
    }
  };

  const resetForm = () => {
    setInvoiceDetails({
      invoice_number: "",
      invoice_date: "",
      hospital_name: "",
      location: "",
      candidate: "",
      des: "",
      amount: "",
      pending_amount: "",
      expected_payment: "",
      rec_amount: "",
      status: "",
      // company: "",
      company: selectedCompany.toString(),
      file: null,
    });
    setEditInvoiceId(null);
  };

  const handleEdit = (invoice) => {
    setInvoiceDetails({
      invoice_number: invoice.invoice_number
        ? String(invoice.invoice_number)
        : "",
      invoice_date: invoice.invoice_date ? String(invoice.invoice_date) : "",
      hospital_name: invoice.hospital_name,
      location: invoice.location || "",
      candidate: invoice.candidate || "",
      des: invoice.des || "",
      amount: invoice.amount,
      pending_amount: invoice.pending_amount,
      expected_payment: invoice.expected_payment,
      rec_amount: invoice.rec_amount,
      status: invoice.status,
      company: invoice.company,
      file: invoice.file,
    });
    setEditInvoiceId(invoice.id);
    setModalOpen(true);
  };

  const onClose = () => {
    setModalOpen(false);
    resetForm();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setInvoiceDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteFile = async (id) => {
    try {
      await axios.delete(getApiUrl(`/Invoice/InvoiceDetail/${id}/`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      fetchInvoicesByCompany();
    } catch (error) {
      console.error("Error deleting file:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100" style={{ display: "flex" }}>
      <CustomNavbar />
      <div className="flex">
        {/* Sidebar */}
        <div className="bg-white shadow-lg p-2">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
            Companies
          </h2>

          <ul className="space-y-3">
            {companies.map((company) => (
              <li
                key={company.id}
                className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition ${selectedCompany === company.id
                    ? "bg-blue-100 text-blue-700"
                    : "hover:bg-gray-200"
                  }`}
                onClick={() => fetchInvoicesByCompany(company.id)}
              >
                <FiFileText className="text-blue-500" />
                <span className="font-medium">{company.name}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Invoice List */}
        <div className="p-3 flex-1">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h1 className="text-4xl font-bold text-blue-500 mb-4">Invoices</h1>
            <div>
              <button
                className="text-white px-4 py-2 rounded bg-blue-500 hover:bg-blue-900"
                // style={{ background: "var(--bg-blue)" }}
                onClick={() => setModalOpen(true)}
              >
                Add Invoice
              </button>

              {/* <button
                className="text-white mx-4 px-4 py-2 rounded bg-blue-500 hover:bg-blue-900"
                onClick={() => setShowAccountDetails(!showAccountDetails)} // Toggle account details visibility
              >
                Account Details
              </button> */}
            </div>
          </div>

          {/* Total Summary Blocks */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-blue-500 rounded shadow text-center text-white">
              <h4 className="text-lg font-semibold">Total Amount</h4>
              <p className="text-xl font-bold">₹{totalAmount.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-green-400 rounded shadow text-center text-white">
              <h4 className="text-lg font-semibold">Total Amount Received</h4>
              <p className="text-xl font-bold">₹{totalReceived.toFixed(2)}</p>
            </div>
            <div className="p-4 bg-red-400 rounded shadow text-center text-white">
              <h4 className="text-lg font-semibold">Total Pending Amount</h4>
              <p className="text-xl font-bold">₹{totalPending.toFixed(2)}</p>
            </div>

            {/* New Block for Bank Limit */}
            <div className="p-4 bg-yellow-500 rounded shadow text-center text-white">
              <h4 className="text-lg font-semibold">Bank Limit</h4>
              <p className="text-xl font-bold">
                {(() => {
                  if (!accountDetails || accountDetails.length === 0) return "N/A";
                  const account = accountDetails[0];
                  
                  if (filterYear !== "" && account.bank_limits && account.bank_limits.length > 0) {
                    const limitForYear = account.bank_limits.find(bl => bl.year === filterYear);
                    if (limitForYear) {
                      return `₹${parseFloat(limitForYear.limit).toFixed(2)}`;
                    }
                  }
                  
                  // Fallback to general account limit if no year filter or no specific limit found
                  return account.acc_limit ? `₹${parseFloat(account.acc_limit).toFixed(2)}` : "N/A";
                })()}
              </p>
            </div>
          </div>

          {/* Account Details Section */}
          <div className="mt-4 bg-white rounded-lg shadow-md mb-5 p-6">
            <h3 className="text-xl font-bold text-blue-600 mb-4">
              Account Details
            </h3>
            <ul className="space-y-4">
              {accountDetails.length > 0 ? (
                accountDetails.map((account, index) => (
                  <li
                    key={index}
                    className="p-4 bg-gray-100 rounded-lg shadow-sm"
                  >
                    <div className="flex flex-col space-y-2">
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-medium text-gray-500">
                          Bank Name
                        </p>
                        <p className="text-base font-semibold text-gray-800">
                          {account.bank_name}
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-medium text-gray-500">
                          IFSC Code
                        </p>
                        <p className="text-base font-semibold text-gray-800">
                          {account.ifsc_code}
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-medium text-gray-500">
                          Branch
                        </p>
                        <p className="text-base font-semibold text-gray-800">
                          {account.branch}
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-medium text-gray-500">
                          Account Number
                        </p>
                        <p className="text-base font-semibold text-gray-800">
                          {account.acc_number}
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-medium text-gray-500">
                          Account Name
                        </p>
                        <p className="text-base font-semibold text-gray-800">
                          {account.acc_name}
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-medium text-gray-500">
                          Signing Authority
                        </p>
                        <p className="text-base font-semibold text-gray-800">
                          {account.signing_authority}
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-medium text-gray-500">
                          Account Limit
                        </p>
                        <p className="text-base font-semibold text-gray-800">
                          {account.acc_limit}
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-medium text-gray-500">
                          Account Type
                        </p>
                        <p className="text-base font-semibold text-gray-800">
                          {account.acc_type}
                        </p>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm font-medium text-gray-500">
                          Contact Number
                        </p>
                        <p className="text-base font-semibold text-gray-800">
                          {account.contact_no}
                        </p>
                      </div>
                    </div>
                  </li>
                ))
              ) : (
                <p className="text-gray-500 text-center">
                  No account details available.
                </p>
              )}
            </ul>
          </div>

          {/* Year Tabs */}
          <div className="mb-4 border-b border-gray-200">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setFilterYear("")}
                className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg border border-b-0 transition-all -mb-px ${
                  filterYear === ""
                    ? "bg-white text-blue-600 border-gray-200 border-b-white z-10"
                    : "bg-gray-50 text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                All Years
              </button>
              {availableYears.map(year => (
                <button
                  key={year}
                  onClick={() => setFilterYear(year)}
                  className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg border border-b-0 transition-all -mb-px ${
                    filterYear === year
                      ? "bg-blue-500 text-white border-blue-500 z-10 shadow-sm"
                      : "bg-gray-50 text-gray-500 border-transparent hover:text-blue-600 hover:bg-blue-50"
                  }`}
                >
                  {year}-{(parseInt(year) + 1).toString().slice(-2)}
                </button>
              ))}
            </div>
          </div>

          {/* Search + Month Filter Row */}
          <div className="mb-5 flex flex-col sm:flex-row items-center gap-3">
            <div className="flex items-center gap-2 flex-[8] w-full bg-white rounded-lg border border-gray-200 px-3 shadow-sm">
              <FiSearch className="text-gray-400 text-lg flex-shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Invoice Number, Hospital Name, Status..."
                className="w-full py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none bg-transparent"
              />
            </div>
            <select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              className="w-full sm:w-28 py-2.5 px-3 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 shadow-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
            >
              <option value="">All Months</option>
              <option value="04">April</option>
              <option value="05">May</option>
              <option value="06">June</option>
              <option value="07">July</option>
              <option value="08">August</option>
              <option value="09">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
              <option value="01">January</option>
              <option value="02">February</option>
              <option value="03">March</option>
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center items-center space-x-2 text-blue-500">
              <AiOutlineLoading3Quarters className="animate-spin text-3xl" />
              <p className="text-xl font-medium">Fetching invoices...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border rounded-lg shadow-md">
                <thead>
                  <tr className="bg-blue-500 text-white text-left">
                    <th className="py-3 px-4">#</th>
                    <th className="py-3 px-4">Invoice Number</th>
                    <th className="py-3 px-4">Hospital Name</th>
                    <th className="py-3 px-4">Invoice Date</th>
                    <th className="py-3 px-4">Amount</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Expand</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.length > 0 ? (
                    filteredInvoices.map((invoice, index) => (
                      <React.Fragment key={invoice.id}>
                        {/* Main Row */}
                        <tr
                          className="hover:bg-gray-50 border-b last:border-none cursor-pointer"
                          onClick={() => toggleRowExpansion(invoice.id)}
                          style={{ fontSize: "14px", color: "black" }}
                        >
                          <td className="py-3 px-4 ">{index + 1}</td>
                          <td className="py-3 px-4 text-gray-800">
                            {invoice.invoice_number}
                          </td>
                          <td className="py-3 px-4 text-gray-800">
                            {invoice.hospital_name}
                          </td>
                          <td className="py-3 px-4 text-gray-800">
                            {invoice.invoice_date}
                          </td>
                          <td className="py-3 px-4 text-gray-800">
                            {invoice.amount}
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-1 rounded ${invoice.status === "Received"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-600"
                                }`}
                            >
                              {invoice.status}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-600">
                            {expandedRowId === invoice.id ? (
                              <FiChevronUp className="text-xl" />
                            ) : (
                              <FiChevronDown className="text-xl" />
                            )}
                          </td>
                        </tr>

                        {/* Expanded Row */}
                        {expandedRowId === invoice.id && (
                          <tr className="bg-gray-50">
                            <td colSpan="7" className="p-3">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <p className="font-semibold text-gray-700">
                                    Candidate:
                                  </p>
                                  <p style={{ fontSize: "14px" }}>
                                    {invoice.candidate}
                                  </p>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-700">
                                    Location:
                                  </p>
                                  <p style={{ fontSize: "14px" }}>
                                    {invoice.location}
                                  </p>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-700">
                                    Description:
                                  </p>
                                  <p style={{ fontSize: "14px" }}>
                                    {invoice.des}
                                  </p>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-700">
                                    Pending Amount:
                                  </p>
                                  <p style={{ fontSize: "14px" }}>
                                    {invoice.pending_amount || "N/A"}
                                  </p>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-700">
                                    Received Amount:
                                  </p>
                                  <p style={{ fontSize: "14px" }}>
                                    {invoice.rec_amount || "N/A"}
                                  </p>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-700">
                                    Expected Payment:
                                  </p>
                                  <p style={{ fontSize: "14px" }}>
                                    {invoice.expected_payment}
                                  </p>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-700">
                                    Note:
                                  </p>
                                  <p style={{ fontSize: "14px" }}>
                                    {invoice.note || "No additional notes."}
                                  </p>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-700">
                                    File:
                                  </p>
                                  {invoice.file ? (
                                    <a
                                      href={getApiUrl(`/media/${invoice.file}`)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-500 hover:underline"
                                      style={{ fontSize: "14px" }}
                                    >
                                      View File
                                    </a>
                                  ) : (
                                    <p
                                      className="text-gray-500"
                                      style={{ fontSize: "14px" }}
                                    >
                                      No file available
                                    </p>
                                  )}
                                </div>
                                <div className="flex gap-4 mt-2">
                                  <button
                                    onClick={() => handleEdit(invoice)}
                                    className="text-yellow-400 hover:text-gray-800"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => {
                                      const confirmDelete = window.confirm(
                                        "Are you sure you want to delete this file? This action cannot be undone."
                                      );
                                      if (confirmDelete) {
                                        handleDeleteFile(invoice.id);
                                      }
                                    }}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="py-8 text-center text-gray-500"
                      >
                        No invoices available for the search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {modalOpen && (
          <div className="fixed inset-0 flex justify-center items-center z-50">
            <div
              className="absolute inset-0 bg-gray-800 bg-opacity-60 backdrop-blur-sm"
              style={{
                width: "100vw",
                height: "150%",
                background: "black",
                backgroundSize: "200% 200vh",
                overflowY: "hidden",
                paddingBottom: "150px",
              }}
            ></div>
            <div
              className="relative bg-white w-4/5 max-w-4xl rounded-lg shadow-lg"
              style={{
                // marginTop: "50px",
                height: "95vh", // Set height to allow scrolling
                overflowY: "auto", // Enable scrolling inside modal
              }}
            >
              <div
                className="overflow-auto max-h-[80vh]"
                style={{ padding: "10px 100px" }}
              >
                <form onSubmit={handleSubmit} className="p-4 space-y-3">
                  <h2 className="text-2xl font-semibold text-center mb-4 text-blue-500">
                    {editFileId ? "Edit Invoice Details" : "Add New Invoice"}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Invoice Number
                      </label>
                      <input
                        type="text"
                        name="invoice_number"
                        value={invoiceDetails.invoice_number}
                        onChange={handleFormChange}
                        placeholder="Enter Invoice Number"
                        required
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Invoice Date
                      </label>
                      <input
                        type="date"
                        name="invoice_date"
                        value={invoiceDetails.invoice_date}
                        onChange={handleFormChange}
                        required
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Hospital Name
                      </label>
                      <input
                        type="text"
                        name="hospital_name"
                        value={invoiceDetails.hospital_name}
                        onChange={handleFormChange}
                        placeholder="Enter Hospital Name"
                        required
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={invoiceDetails.location}
                        onChange={handleFormChange}
                        placeholder="Enter Location"
                        required
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Candidate
                      </label>
                      <input
                        type="text"
                        name="candidate"
                        value={invoiceDetails.candidate}
                        onChange={handleFormChange}
                        placeholder="Enter Candidate Name"
                        required
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Designation
                      </label>
                      <input
                        type="text"
                        name="des"
                        value={invoiceDetails.des}
                        onChange={handleFormChange}
                        placeholder="Enter Designation"
                        required
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Amount
                      </label>
                      <input
                        type="text"
                        name="amount"
                        value={invoiceDetails.amount}
                        onChange={handleFormChange}
                        placeholder="Enter Amount"
                        required
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Pending Amount
                      </label>
                      <input
                        type="text"
                        name="pending_amount"
                        value={invoiceDetails.pending_amount}
                        onChange={handleFormChange}
                        placeholder="Enter Pending Amount"
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Expected Payment Date
                      </label>
                      <input
                        type="date"
                        name="expected_payment"
                        value={invoiceDetails.expected_payment}
                        onChange={handleFormChange}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Received Amount
                      </label>
                      <input
                        type="text"
                        name="rec_amount"
                        value={invoiceDetails.rec_amount}
                        onChange={handleFormChange}
                        placeholder="Enter Received Amount"
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Status
                      </label>
                      <select
                        name="status"
                        onChange={handleFormChange}
                        value={invoiceDetails.status}
                        className="w-full p-2 border rounded"
                      >
                        <option value="">--Select Status--</option>
                        <option value="Received">Received</option>
                        <option value="Pending">Pending</option>
                        {/* <option value="Hold">Hold</option>
                      <option value="Advance Received">Advance Received</option> */}
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Company
                      </label>
                      <select
                        id="company"
                        name="company"
                        value={invoiceDetails.company}
                        onChange={handleFormChange}
                        required
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select Company</option>
                        {companies.map((company) => (
                          <option key={company.id} value={String(company.id)}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        Note
                      </label>
                      <input
                        type="text"
                        name="note"
                        value={invoiceDetails.note}
                        onChange={handleFormChange}
                        placeholder="Enter Note"
                        className="w-full p-2 border rounded"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">
                        File
                      </label>
                      <input
                        type="file"
                        name="file"
                        onChange={(e) => {
                          setInvoiceDetails((prev) => ({
                            ...prev,
                            file: e.target.files[0],
                          }));
                        }}
                        className="w-full p-2 border rounded"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded"
                    >
                      {editFileId ? "Update" : "Save"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceList;
